import { useEffect, useRef, useState } from 'react';

import { errorMessage } from '@/shared/api/errors';

import { fileProblem, type OwnFile } from './outgoing';
import { useUploadOutgoingFile } from './useEmails';

/**
 * The person's own files of a message (contract v10.41), for the mail manager's sheet and
 * for «Enviar por correo» of a quote (contract v10.59): each one goes up as soon as it is
 * chosen (`POST /api/outgoing/uploads`), and «Enviar» carries only their ids.
 */
export function useOwnFiles(limit: number) {
  const upload = useUploadOutgoingFile();
  const [files, setFiles] = useState<OwnFile[]>([]);
  const nextKey = useRef(0);

  function replace(key: string, file: OwnFile): void {
    // A file removed while it was going up is not there to replace: it stays removed.
    setFiles((current) => current.map((each) => (each.key === key ? file : each)));
  }

  function add(list: FileList): void {
    for (const file of Array.from(list)) {
      nextKey.current += 1;
      const key = `own-${nextKey.current}`;
      const base = { key, name: file.name, size: file.size };
      const problem = fileProblem(file.size, limit);
      if (problem) {
        setFiles((current) => [...current, { ...base, state: 'failed', error: problem }]);
        continue;
      }
      setFiles((current) => [...current, { ...base, state: 'uploading' }]);
      upload.mutateAsync(file).then(
        (uploaded) =>
          replace(key, {
            key,
            name: uploaded.filename,
            size: uploaded.size_bytes,
            state: 'done',
            id: uploaded.id,
          }),
        (error: unknown) => replace(key, { ...base, state: 'failed', error: errorMessage(error) }),
      );
    }
  }

  function remove(key: string): void {
    setFiles((current) => current.filter((file) => file.key !== key));
  }

  return {
    files,
    add,
    remove,
    uploading: files.some((file) => file.state === 'uploading'),
    failed: files.some((file) => file.state === 'failed'),
    ids: files.flatMap((file) => (file.state === 'done' ? [file.id] : [])),
  };
}

/**
 * A file dropped beside the form (the footer, the backdrop) would have the browser open it
 * in the tab, and the sheet would be gone with everything written in it.
 */
export function useHoldStrayDrops(): void {
  useEffect(() => {
    function hold(event: globalThis.DragEvent): void {
      if (event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files')) {
        event.preventDefault();
      }
    }
    window.addEventListener('dragover', hold);
    window.addEventListener('drop', hold);
    return () => {
      window.removeEventListener('dragover', hold);
      window.removeEventListener('drop', hold);
    };
  }, []);
}
